import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './NFTDetails.css';

const NFTDetails = ({ nft, onClose, walletConnected, account }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('activity');
  const [nftDetails, setNftDetails] = useState(null);
  const [bestOffer, setBestOffer] = useState(null);
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    if (nft) {
      fetchNFTDetails();
      fetchBestOffer();
      fetchListings();
      fetchOffers();
      fetchActivity();
    }
  }, [nft]);

  const fetchNFTDetails = async () => {
    setLoading(true);
    try {
      const chain = nft.chain || 'ethereum';
      const contract = nft.contract;
      const identifier = nft.identifier || nft.id;
      
      const url = `https://api.opensea.io/api/v2/chain/${chain}/contract/${contract}/nfts/${identifier}`;
      const response = await axios.get(url);
      
      if (response.data && response.data.nft) {
        setNftDetails(response.data.nft);
      }
    } catch (error) {
      console.error('Error fetching NFT details:', error);
      setError('Failed to load NFT details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBestOffer = async () => {
    try {
      const collection = nft.collection;
      const identifier = nft.identifier || nft.id;
      
      const url = `https://api.opensea.io/api/v2/offers/collection/${collection}/nfts/${identifier}/best`;
      const response = await axios.get(url);
      
      if (response.data && response.data.price) {
        setBestOffer(response.data);
      }
    } catch (error) {
      console.error('Error fetching best offer:', error);
      // Don't set error state here as this is not critical
    }
  };

  const fetchListings = async () => {
    try {
      const chain = nft.chain || 'ethereum';
      const contract = nft.contract;
      const identifier = nft.identifier || nft.id;
      
      const url = `https://api.opensea.io/api/v2/chain/${chain}/contract/${contract}/nfts/${identifier}/listings`;
      const response = await axios.get(url);
      
      if (response.data && response.data.listings) {
        setListings(response.data.listings);
      }
    } catch (error) {
      console.error('Error fetching listings:', error);
      // Don't set error state here as this is not critical
    }
  };

  const fetchOffers = async () => {
    try {
      const collection = nft.collection;
      const identifier = nft.identifier || nft.id;
      
      const url = `https://api.opensea.io/api/v2/offers/collection/${collection}/nfts/${identifier}`;
      const response = await axios.get(url);
      
      if (response.data && response.data.offers) {
        setOffers(response.data.offers);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      // Don't set error state here as this is not critical
    }
  };

  const fetchActivity = async () => {
    try {
      const chain = nft.chain || 'ethereum';
      const contract = nft.contract;
      const identifier = nft.identifier || nft.id;
      
      const url = `https://api.opensea.io/api/v2/events/chain/${chain}/contract/${contract}/nfts/${identifier}`;
      const response = await axios.get(url);
      
      if (response.data && response.data.asset_events) {
        setActivities(response.data.asset_events);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
      // Don't set error state here as this is not critical
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  const formatPrice = (price, currency = 'ETH', decimals = 18) => {
    if (!price) return 'N/A';
    
    const value = parseFloat(price) / Math.pow(10, decimals);
    return `${value.toFixed(2)} ${currency}`;
  };

  const renderActivityTab = () => (
    <div className="details-tab-content">
      <h3>Activity</h3>
      {activities.length === 0 ? (
        <p className="no-data">No activity found for this NFT</p>
      ) : (
        <table className="activity-table">
          <thead>
            <tr>
              <th>Event</th>
              <th>Price</th>
              <th>From</th>
              <th>To</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {activities.map((event, index) => (
              <tr key={index}>
                <td>{event.event_type}</td>
                <td>
                  {event.payment ? formatPrice(
                    event.payment.quantity,
                    event.payment.symbol,
                    event.payment.decimals
                  ) : 'N/A'}
                </td>
                <td>{formatAddress(event.from_address || event.seller)}</td>
                <td>{formatAddress(event.to_address || event.buyer)}</td>
                <td>{formatDate(event.event_timestamp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderListingsTab = () => (
    <div className="details-tab-content">
      <h3>Listings</h3>
      {listings.length === 0 ? (
        <p className="no-data">No listings found for this NFT</p>
      ) : (
        <table className="listings-table">
          <thead>
            <tr>
              <th>Price</th>
              <th>Seller</th>
              <th>Expiration</th>
            </tr>
          </thead>
          <tbody>
            {listings.map((listing, index) => (
              <tr key={index}>
                <td>
                  {formatPrice(
                    listing.price.current.value,
                    listing.price.current.currency
                  )}
                </td>
                <td>{formatAddress(listing.maker)}</td>
                <td>{formatDate(listing.expiration_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderOffersTab = () => (
    <div className="details-tab-content">
      <h3>Offers</h3>
      {offers.length === 0 ? (
        <p className="no-data">No offers found for this NFT</p>
      ) : (
        <table className="offers-table">
          <thead>
            <tr>
              <th>Price</th>
              <th>From</th>
              <th>Expiration</th>
            </tr>
          </thead>
          <tbody>
            {offers.map((offer, index) => (
              <tr key={index}>
                <td>
                  {formatPrice(
                    offer.price.value,
                    offer.price.currency,
                    offer.price.decimals
                  )}
                </td>
                <td>{formatAddress(offer.maker)}</td>
                <td>{formatDate(offer.expiration_date)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  const renderInfoTab = () => (
    <div className="details-tab-content">
      <h3>Info</h3>
      {!nftDetails ? (
        <p className="no-data">No details found for this NFT</p>
      ) : (
        <div className="nft-info-details">
          <div className="info-section">
            <h4>Basic Info</h4>
            <p><strong>Name:</strong> {nftDetails.name || 'N/A'}</p>
            <p><strong>Collection:</strong> {nftDetails.collection || 'N/A'}</p>
            <p><strong>Token ID:</strong> {nftDetails.identifier || 'N/A'}</p>
            <p><strong>Contract:</strong> {nftDetails.contract || 'N/A'}</p>
            <p><strong>Token Standard:</strong> {nftDetails.token_standard || 'N/A'}</p>
            <p><strong>Creator:</strong> {formatAddress(nftDetails.creator)}</p>
          </div>
          
          {nftDetails.description && (
            <div className="info-section">
              <h4>Description</h4>
              <p>{nftDetails.description}</p>
            </div>
          )}
          
          {nftDetails.traits && nftDetails.traits.length > 0 && (
            <div className="info-section">
              <h4>Properties</h4>
              <div className="traits-grid">
                {nftDetails.traits.map((trait, index) => (
                  <div key={index} className="trait-item">
                    <div className="trait-type">{trait.trait_type}</div>
                    <div className="trait-value">{trait.value}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="info-section">
            <h4>Links</h4>
            <p>
              <a 
                href={nftDetails.opensea_url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="external-link"
              >
                View on OpenSea
              </a>
            </p>
            {nftDetails.metadata_url && (
              <p>
                <a 
                  href={nftDetails.metadata_url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="external-link"
                >
                  View Metadata
                </a>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const getTabContent = () => {
    switch (activeTab) {
      case 'activity':
        return renderActivityTab();
      case 'listings':
        return renderListingsTab();
      case 'offers':
        return renderOffersTab();
      case 'info':
        return renderInfoTab();
      default:
        return renderActivityTab();
    }
  };

  if (!nft) return null;

  // Get the current listing price
  const currentPrice = nft.price ? `${nft.price} ${nft.priceSymbol || 'ETH'}` : 'Not listed';
  
  // Get the best offer price
  const topOfferPrice = bestOffer?.price 
    ? formatPrice(bestOffer.price.value, bestOffer.price.currency, bestOffer.price.decimals) 
    : 'No offers';

  return (
    <div className="nft-details-overlay">
      <div className="nft-details-container">
        <div className="nft-details-header">
          <h2>{nft.name || `#${nft.identifier || nft.id}`}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="nft-details-content">
          <div className="nft-details-left">
            <div className="nft-details-image">
              <img 
                src={nft.image_url || nft.display_image_url || nft.image || 'https://via.placeholder.com/500'}
                alt={nft.name || 'NFT'}
                onError={(e) => {
                  e.target.onerror = null; 
                  e.target.src = 'https://via.placeholder.com/500?text=Image+Not+Available';
                }}
              />
            </div>
            {nftDetails?.description && (
              <div className="nft-description">
                <p>{nftDetails.description}</p>
              </div>
            )}
          </div>
          
          <div className="nft-details-right">
            <div className="price-section">
              <div className="price-box">
                <h3>Price:</h3>
                <div className="price-value">Ξ {currentPrice}</div>
                <div className="price-source">on OpenSea</div>
                
                {walletConnected && nft.price && (
                  <button className="action-button primary-action">Buy Now</button>
                )}
              </div>
              
              <div className="price-box">
                <h3>Top offer:</h3>
                <div className="price-value">Ξ {topOfferPrice}</div>
                <div className="price-source">on OpenSea</div>
                
                {walletConnected && (
                  <button className="action-button secondary-action">Make offer</button>
                )}
              </div>
            </div>
            
            <div className="tabs-section">
              <div className="tabs-header">
                <button 
                  className={`tab-button ${activeTab === 'activity' ? 'active' : ''}`}
                  onClick={() => setActiveTab('activity')}
                >
                  Activity
                </button>
                <button 
                  className={`tab-button ${activeTab === 'listings' ? 'active' : ''}`}
                  onClick={() => setActiveTab('listings')}
                >
                  Listings
                </button>
                <button 
                  className={`tab-button ${activeTab === 'offers' ? 'active' : ''}`}
                  onClick={() => setActiveTab('offers')}
                >
                  Offers
                </button>
                <button 
                  className={`tab-button ${activeTab === 'info' ? 'active' : ''}`}
                  onClick={() => setActiveTab('info')}
                >
                  Info
                </button>
              </div>
              
              <div className="tab-content">
                {loading ? (
                  <div className="loading-indicator">Loading...</div>
                ) : error ? (
                  <div className="error-message">{error}</div>
                ) : (
                  getTabContent()
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NFTDetails;